/**
 * #1221 task 4a — the CROSS-TAB mutual-exclusion seam for refresh-token rotation.
 *
 * ## Why this exists
 *
 * The server ROTATES the refresh token, so two tabs that read the same stored token and
 * exchange it concurrently produce one winner and one casualty: the loser presents a token the
 * winner already spent, gets a 401, and clears its session. `http/client.ts`'s `refreshInFlight`
 * coalesces this WITHIN a tab and is blind ACROSS tabs, which is where the surviving race lives.
 *
 * ⛔ Serialisation, NOT deduplication. N tabs still perform N rotations, sequentially, and each
 * is valid. Collapsing them into one shared exchange would mean broadcasting the ACCESS token
 * between tabs, and `http/client.ts:21-26` deliberately keeps that token in module memory so an
 * XSS that reads storage gets a rotating credential it must exchange rather than a replayable
 * bearer. The property being bought here is "no tab is left holding a dead session", nothing more.
 *
 * ## ⛔ `navigator.locks` is `null` under happy-dom, and BOTH presence checks pass over it
 *
 * Measured in this package's own vitest environment, with `typeof localStorage === 'object'` as
 * an in-invocation positive control:
 *
 *     typeof navigator.locks    -> 'object'   ⛔ because typeof null === 'object'
 *     'locks' in navigator      -> true       ⛔ the property EXISTS
 *     navigator.locks === null  -> true
 *     navigator.locks.request() -> TypeError: Cannot read properties of null (reading 'request')
 *
 * ⇒ The detect below is `!= null` plus a callable check. `typeof`, `in`, and optional chaining
 * on the property (`navigator.locks?.request`) all fail to discriminate a `null` value. Only
 * invoking does — which is why this was measured rather than assumed.
 *
 * ## Why a SEAM and not a direct call
 *
 * Because of the above, the Web Locks branch cannot execute in this test environment at all, so
 * production code calling `navigator.locks` directly would be structurally uncoverable and every
 * test would silently exercise the fallback. The seam lets a suite drive a real serialising fake
 * and assert the logic that depends on a lock. House precedent: `useAuth.ts`'s `navigateExternal`
 * option, which exists for exactly this reason ("the one seam onto `window.location`, so `logout`
 * is assertable without touching the global").
 */

/** Run `fn` with no other holder of `name` running concurrently, in this browser profile. */
export type RunExclusive = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

/** The lock name. One name for the whole rotation critical section. */
export const REFRESH_LOCK_NAME = 'zion.cp.auth.refresh';

/**
 * How long to wait for the lock before giving up and refreshing anyway.
 *
 * ⛔ FAIL TOWARD ATTEMPTING THE REFRESH, never toward hanging. A refresh that never resolves
 * because some other context wedged while holding the lock is strictly worse than the race this
 * file exists to prevent: the race costs one tab its session, a hang costs every tab every
 * request. Ten seconds is far longer than a healthy rotation and far shorter than a user
 * noticing a stuck page.
 *
 * ⚠️ A tab that CRASHES while holding is already handled by the browser — Web Locks are released
 * when the holding context goes away. This timeout is for the wedged-but-alive case.
 */
export const LOCK_ACQUIRE_TIMEOUT_MS = 10_000;

interface LockManagerLike {
  request<T>(
    name: string,
    options: { signal: AbortSignal },
    fn: () => Promise<T>,
  ): Promise<T>;
}

/**
 * The live Web Locks manager, or `null` when this browser (or test environment) has none.
 *
 * ⛔ `!= null` AND a callable `request`. See the header: happy-dom sets the property to `null`,
 * so a truthiness test on `navigator` or a `typeof`/`in` check on the property both pass.
 */
function lockManager(): LockManagerLike | null {
  const candidate = (navigator as Navigator & { locks?: unknown }).locks;
  if (candidate == null) return null;
  const request = (candidate as { request?: unknown }).request;
  return typeof request === 'function' ? (candidate as LockManagerLike) : null;
}

/** What production runs: Web Locks where available, an unserialised call where not. */
export async function defaultRunExclusive<T>(
  name: string,
  fn: () => Promise<T>,
): Promise<T> {
  const manager = lockManager();
  // No Web Locks ⇒ today's behaviour, unchanged. Degrading to "no cross-tab coordination" is
  // exactly the pre-#1221 state, so an old browser is no worse off than it is now — whereas
  // refusing to refresh would sign it out.
  if (manager === null) return fn();

  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, LOCK_ACQUIRE_TIMEOUT_MS);

  // ⛔ Tracks whether `fn` was ENTERED, and it is what makes the fallback below safe. The
  // AbortSignal cancels ACQUISITION only — once the lock is granted the request cannot be
  // aborted — so an `AbortError` normally means `fn` never ran. But `fn` itself could reject
  // with an `AbortError` of its own (a cancelled fetch), and re-running it on that basis would
  // perform the rotation TWICE. The flag distinguishes the two without inspecting error shapes.
  let entered = false;
  try {
    return await manager.request(name, { signal: controller.signal }, async () => {
      entered = true;
      return fn();
    });
  } catch (cause) {
    if (entered) throw cause; // `fn`'s own failure — the caller owns it.
    // Acquisition failed or timed out. Attempt the refresh unserialised: the race is a risk,
    // a guaranteed sign-out is a certainty.
    return fn();
  } finally {
    clearTimeout(timer);
  }
}

let override: RunExclusive | null = null;

/**
 * Acquire the cross-tab lock named `name` and run `fn`.
 *
 * Indirects through the override so a suite can supply a real serialising fake — see the header
 * for why the production primitive cannot run under happy-dom.
 */
export function runExclusive<T>(name: string, fn: () => Promise<T>): Promise<T> {
  return override === null ? defaultRunExclusive(name, fn) : override(name, fn);
}

/** TEST-ONLY. Install a lock implementation. */
export function __setRunExclusiveForTest(fn: RunExclusive): void {
  override = fn;
}

/** TEST-ONLY. Restore the production implementation. */
export function __resetRunExclusiveForTest(): void {
  override = null;
}
